import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { teacherAPI } from '../../services/api';
import './ManageStudents.css';

function ManageStudents({ user, onLogout }) {
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');

  // New student form
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    username: '',
    password: '',
    fullName: '',
    groupId: ''
  });

  // New group form
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [studentsRes, groupsRes] = await Promise.all([
        teacherAPI.getStudents(),
        teacherAPI.getGroups()
      ]);
      setStudents(studentsRes.data.students);
      setGroups(groupsRes.data.groups);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await teacherAPI.createStudent(newStudent);
      setSuccess('Student created successfully');
      setNewStudent({ username: '', password: '', fullName: '', groupId: '' });
      setShowAddStudent(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create student');
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await teacherAPI.createGroup(newGroupName);
      setSuccess('Group created successfully');
      setNewGroupName('');
      setShowAddGroup(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
    }
  };

  const handleMoveStudent = async (studentId, groupId) => {
    setError('');
    setSuccess('');

    try {
      await teacherAPI.moveStudentToGroup(studentId, groupId);
      setSuccess('Student moved successfully');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to move student');
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          student.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = selectedGroup === 'all' || student.group_name === selectedGroup;
    return matchesSearch && matchesGroup;
  });

  const uniqueGroups = [...new Set(students.map(s => s.group_name).filter(Boolean))];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="manage-students">
        <h1>Manage Students & Groups</h1>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Groups Section */}
        <div className="card">
          <div className="card-header">
            <h2>Groups</h2>
            <button className="btn btn-primary" onClick={() => setShowAddGroup(true)}>
              Add Group
            </button>
          </div>

          {showAddGroup && (
            <form onSubmit={handleAddGroup} className="add-form">
              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-success">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddGroup(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="groups-grid">
            {groups.map(group => (
              <div key={group.id} className="group-card">
                <h3>{group.name}</h3>
                <p>{group.student_count} {group.student_count === 1 ? 'student' : 'students'}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Students Section */}
        <div className="card">
          <div className="card-header">
            <h2>Students</h2>
            <button className="btn btn-primary" onClick={() => setShowAddStudent(true)}>
              Add Student
            </button>
          </div>

          <div className="filters-section">
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="group-filter"
            >
              <option value="all">All Groups</option>
              {uniqueGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>

          {showAddStudent && (
            <form onSubmit={handleAddStudent} className="add-form">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={newStudent.username}
                  onChange={(e) => setNewStudent({...newStudent, username: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={newStudent.password}
                  onChange={(e) => setNewStudent({...newStudent, password: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={newStudent.fullName}
                  onChange={(e) => setNewStudent({...newStudent, fullName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Group (Optional)</label>
                <select
                  value={newStudent.groupId}
                  onChange={(e) => setNewStudent({...newStudent, groupId: e.target.value})}
                >
                  <option value="">No group</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-success">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddStudent(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          <table className="table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Group</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(student => (
                <tr key={student.id}>
                  <td>{student.username}</td>
                  <td>{student.full_name}</td>
                  <td>{student.group_name || '-'}</td>
                  <td>
                    <select
                      value={student.group_id || ''}
                      onChange={(e) => handleMoveStudent(student.id, e.target.value || null)}
                      className="group-select"
                    >
                      <option value="">No group</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default ManageStudents;
